import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import {
  ETransactionStatus,
  ITransactionDeliveryRecord,
  ITransaction,
  ITransactionHeader,
  ITransactionLineItem,
  ITransactionPaymentRecord,
  ETransactionType,
  ISaleTransactionTodayReports
} from '../../models/transaction.model';
import { ISalesSummary } from '../../models/reports.model';
import { ISibasiHTTPResponse } from 'src/app/common/model/sibasiHTTPResponse.model';
import { IPagination } from 'src/app/common/model/util.model';

@Injectable({
  providedIn: 'root'
})
export class SalesService {
  constructor(private http: HttpClient) {}

  createSale(sale: any): Observable<ISibasiHTTPResponse<ITransaction>> {
    return this.http.post<ISibasiHTTPResponse<ITransaction>>(
      `${environment.api_url}pos/sale`,
      sale
    );
  }

  updateSaleLineItems(
    transactionId: string,
    sale: any
  ): Observable<ISibasiHTTPResponse<ITransaction>> {
    return this.http.post<ISibasiHTTPResponse<ITransaction>>(
      `${environment.api_url}transactionLineItem/replace/all/${transactionId}`,
      sale
    );
  }

  appendTransactionPaymentRecord(
    paymentRecord: any
  ): Observable<ISibasiHTTPResponse<ITransactionPaymentRecord>> {
    return this.http.post<ISibasiHTTPResponse<ITransactionPaymentRecord>>(
      `${environment.api_url}transactionPaymentRecord`,
      paymentRecord
    );
  }

  appendTransactionDeliveryDetails(
    deliveryDetails: Partial<ITransactionDeliveryRecord>
  ): Observable<ISibasiHTTPResponse<ITransactionDeliveryRecord>> {
    return this.http.post<ISibasiHTTPResponse<ITransactionDeliveryRecord>>(
      `${environment.api_url}pos/delivery`,
      deliveryDetails
    );
  }

  editTransactionDeliveryDetails(
    deliveryDetails: Partial<ITransactionDeliveryRecord>,
    deliveryRecordId: string | undefined
  ): Observable<ISibasiHTTPResponse<ITransactionDeliveryRecord>> {
    return this.http.post<ISibasiHTTPResponse<ITransactionDeliveryRecord>>(
      `${environment.api_url}pos/delivery/update/${deliveryRecordId}`,
      deliveryDetails
    );
  }

  updateSale(
    transactionHeader: Partial<ITransactionHeader>
  ): Observable<ISibasiHTTPResponse<ITransaction>> {
    return this.http.post<ISibasiHTTPResponse<ITransaction>>(
      `${environment.api_url}pos/sale/${transactionHeader?._id}`,
      transactionHeader
    );
  }

  getSales(): Observable<ISibasiHTTPResponse<ITransaction[]>> {
    return this.http.get<ISibasiHTTPResponse<ITransaction[]>>(
      `${environment.api_url}pos/sale`
    );
  }

  getCustomerSaleHistory(
    customerId: string
  ): Observable<ISibasiHTTPResponse<ITransaction[]>> {
    return this.http.get<ISibasiHTTPResponse<ITransaction[]>>(
      `${environment.api_url}pos/sale/all/customer/${customerId}`
    );
  }

  getCustomerAllTransactionHistory(
    customerId: string,
    pagination?: any
  ): Observable<ISibasiHTTPResponse<ITransactionHeader[]>> {
    return this.http
      .post<
        ISibasiHTTPResponse<ITransactionHeader[]>
      >(`${environment.api_url}pos/reports/transactions/paginated/${customerId}`, pagination ? pagination : {})
      .pipe(
        map((res) => {
          const transaction = res.result?.length > 0 ? res.result.slice().reverse() : [];
          return {
            ...res,
            result: transaction
          };
        })
      );
  }

  getAllCustomerTransactionHistoryCount(
    customerId: any
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.http.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}pos/reports/transactions/count/all/${customerId}`
    );
  }

  getAllCustomerSearchedTransactionHistoryCount(
    searchParam: string,
    customerId: any
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.http.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}pos/reports/transactions/count/searched/${customerId}`,
      { searchParam }
    );
  }

  getSaleById(transactionId: string): Observable<ISibasiHTTPResponse<ITransaction>> {
    return this.http.get<ISibasiHTTPResponse<ITransaction>>(
      `${environment.api_url}pos/sale/${transactionId}`
    );
  }

  getSaleItems(sale: any) {
    return this.http.get(`${environment.api_url}pos/sale/items/${sale._id}`);
  }
  getSaleSummary(sale: any) {
    return this.http.get(`${environment.api_url}pos/sale/summary`);
  }

  getAllSalesCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.http.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}pos/sale/all/count`
    );
  }

  getAllSearchedSalesCount(searchParam: string): Observable<ISibasiHTTPResponse<number>> {
    return this.http.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}pos/sale/all/count/searched`,
      { searchParam }
    );
  }

  getPaginatedSales(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<IPagination<ITransactionHeader[]>>> {
    return this.http.post<ISibasiHTTPResponse<IPagination<ITransactionHeader[]>>>(
      `${environment.api_url}pos/sale/all/paginated`,
      pagination
    );
  }

  getPOSLoggedInMemberSavedTransactions(
    memberId: string | undefined
  ): Observable<ISibasiHTTPResponse<IPagination<ITransactionHeader[]>>> {
    return this.http
      .get<
        ISibasiHTTPResponse<IPagination<ITransactionHeader[]>>
      >(`${environment.api_url}desktop/all/member/${memberId}`)
      .pipe(
        map((res) => {
          const transaction =
            res.result?.data.length > 0 ? res.result.data.slice().reverse() : [];
          return {
            ...res,
            data: transaction
          };
        })
      );
  }

  removeContactFromParentContact(
    customerId: string,
    keyContactId: string
  ): Observable<ISibasiHTTPResponse<ITransaction[]>> {
    return this.http.get<ISibasiHTTPResponse<ITransaction[]>>(
      `${environment.api_url}pos/customer/remove/keyContacts/${customerId}/${keyContactId}`
    );
  }
  // summary sales
  getReportSummary(
    key: any,
    details: {}
  ): Observable<
    ISibasiHTTPResponse<
      [
        {
          data: [];
          totalNumberOfItems: number;
          totalNumberOfTransactions: number;
          totalSales: number;
          label: string;
        }
      ]
    >
  > {
    return this.http.post<
      ISibasiHTTPResponse<
        [
          {
            data: [];
            totalNumberOfItems: number;
            totalNumberOfTransactions: number;
            totalSales: number;
            label: string;
          }
        ]
      >
    >(`${environment.api_url}pos/reports/generateReport/${key}`, details);
  }

  getWebReportSummary(details: {}): Observable<ISibasiHTTPResponse<ISalesSummary>> {
    return this.http.post<ISibasiHTTPResponse<ISalesSummary>>(
      `${environment.api_url}pos/reports/saleSummary`,
      details
    );
  }

  getReportKey(): Observable<ISibasiHTTPResponse<string>> {
    return this.http.get<ISibasiHTTPResponse<string>>(
      `${environment.api_url}pos/reports/generateKey`
    );
  }

  sendReceiptEmail(
    transactionId: string | undefined,
    email: any
  ): Observable<ISibasiHTTPResponse<{}>> {
    return this.http.post<ISibasiHTTPResponse<{}>>(
      `${environment.api_url}pos/sale/share/email/${transactionId}`,
      email
    );
  }
  // ******************************************INVOICE SERVICE****************************************************

  generateInvoice(invoice: any): Observable<ISibasiHTTPResponse<ITransaction>> {
    return this.http.post<ISibasiHTTPResponse<ITransaction>>(
      `${environment.api_url}pos/invoice`,
      invoice
    );
  }
  customizeInvoice(invoice: any): Observable<ISibasiHTTPResponse<ITransaction>> {
    return this.http.post<ISibasiHTTPResponse<ITransaction>>(
      `${environment.api_url}pos/invoice`,
      invoice
    );
  }
  getAllInvoices(): Observable<ISibasiHTTPResponse<ITransaction[]>> {
    return this.http.get<ISibasiHTTPResponse<ITransaction[]>>(
      `${environment.api_url}pos/invoice/all`
    );
  }
  getInvoiceById(invoiceId: string): Observable<ISibasiHTTPResponse<ITransaction>> {
    return this.http.get<ISibasiHTTPResponse<ITransaction>>(
      `${environment.api_url}pos/invoice/one/${invoiceId}`
    );
  }
  getAllCustomerInvoices(
    customerId: string | undefined
  ): Observable<ISibasiHTTPResponse<ITransaction[]>> {
    return this.http.get<ISibasiHTTPResponse<ITransaction[]>>(
      `${environment.api_url}pos/invoice/all/${customerId}`
    );
  }
  updateInvoice(
    invoice: any,
    invoiceId: string | undefined
  ): Observable<ISibasiHTTPResponse<ITransaction>> {
    return this.http.post<ISibasiHTTPResponse<ITransaction>>(
      `${environment.api_url}pos/invoice/update/${invoiceId}`,
      invoice
    );
  }
  deleteInvoice(invoiceId: string): Observable<ISibasiHTTPResponse<ITransactionHeader>> {
    return this.http.delete<ISibasiHTTPResponse<ITransactionHeader>>(
      `${environment.api_url}pos/invoice/delete/${invoiceId}`
    );
  }
  actionInvoice(
    invoiceId: string,
    action: ETransactionStatus
  ): Observable<ISibasiHTTPResponse<ITransactionHeader>> {
    return this.http.post<ISibasiHTTPResponse<ITransactionHeader>>(
      `${environment.api_url}pos/invoice/delete/${invoiceId}`,
      action
    );
  }
  getInvoiceFromPreviousTransaction(
    transactionId: string,
    invoiceDueDate: any
  ): Observable<ISibasiHTTPResponse<ITransactionHeader>> {
    return this.http.get<ISibasiHTTPResponse<ITransactionHeader>>(
      `${environment.api_url}pos/invoice/transaction/${transactionId}/${invoiceDueDate}`
    );
  }

  generateInvoiceFromSale(
    invoiceDetails: any
  ): Observable<ISibasiHTTPResponse<ITransaction>> {
    return this.http.post<ISibasiHTTPResponse<ITransaction>>(
      `${environment.api_url}pos/invoice/new/posSale/covert`,
      invoiceDetails
    );
  }
  generateInvoiceFromQuote(
    invoiceDetails: any
  ): Observable<ISibasiHTTPResponse<ITransaction>> {
    return this.http.post<ISibasiHTTPResponse<ITransaction>>(
      `${environment.api_url}pos/quote/new/invoice/covert`,
      invoiceDetails
    );
  }

  getAllInvoicesCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.http.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}pos/invoice/count/all`
    );
  }

  getAllSearchedInvoicesCount(
    searchParam: string
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.http.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}pos/invoice/count/searched`,
      { searchParam }
    );
  }

  getPaginatedInvoices(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<IPagination<ITransactionHeader[]>>> {
    return this.http.post<ISibasiHTTPResponse<IPagination<ITransactionHeader[]>>>(
      `${environment.api_url}pos/invoice/paginated`,
      pagination ? pagination : {}
    );
  }

  updateTransactionLineItem(
    transactionLineItem: ITransactionLineItem
  ): Observable<ISibasiHTTPResponse<ITransactionLineItem>> {
    return this.http.post<ISibasiHTTPResponse<ITransactionLineItem>>(
      `${environment.api_url}transactionLineItem/${transactionLineItem?._id}`,
      transactionLineItem
    );
  }

  deleteTransactionLineItem(
    transactionLineItem: any
  ): Observable<ISibasiHTTPResponse<ITransactionLineItem>> {
    return this.http.post<ISibasiHTTPResponse<ITransactionLineItem>>(
      `${environment.api_url}pos/invoice/lineItems/delete`,
      transactionLineItem
    );
  }

  addTransactionLineItem(
    transactionLineItem: any
  ): Observable<ISibasiHTTPResponse<ITransactionLineItem>> {
    return this.http.post<ISibasiHTTPResponse<ITransactionLineItem>>(
      `${environment.api_url}transactionLineItem`,
      transactionLineItem
    );
  }

  sendInvoiceEmail(
    transactionId: string | undefined,
    email: any
  ): Observable<ISibasiHTTPResponse<{}>> {
    return this.http.post<ISibasiHTTPResponse<{}>>(
      `${environment.api_url}pos/invoice/share/email/${transactionId}`,
      email
    );
  }
  // ******************************************QUOTES SERVICE****************************************************

  generateQuote(quote: any): Observable<ISibasiHTTPResponse<ITransaction>> {
    return this.http.post<ISibasiHTTPResponse<ITransaction>>(
      `${environment.api_url}pos/quote`,
      quote
    );
  }
  getAllQuotes(): Observable<ISibasiHTTPResponse<ITransaction[]>> {
    return this.http.get<ISibasiHTTPResponse<ITransaction[]>>(
      `${environment.api_url}pos/quote/all`
    );
  }
  getQuoteById(quoteId: string): Observable<ISibasiHTTPResponse<ITransaction>> {
    return this.http.get<ISibasiHTTPResponse<ITransaction>>(
      `${environment.api_url}pos/quote/one/${quoteId}`
    );
  }
  getAllCustomerQuotes(
    customerId: string
  ): Observable<ISibasiHTTPResponse<ITransaction[]>> {
    return this.http.get<ISibasiHTTPResponse<ITransaction[]>>(
      `${environment.api_url}pos/quote/all/${customerId}`
    );
  }
  updateQuote(
    quote: any,
    quoteId: string | undefined
  ): Observable<ISibasiHTTPResponse<ITransaction>> {
    return this.http.post<ISibasiHTTPResponse<ITransaction>>(
      `${environment.api_url}pos/quote/update/${quoteId}`,
      quote
    );
  }
  deleteQuote(quoteId: string): Observable<ISibasiHTTPResponse<ITransactionHeader>> {
    return this.http.delete<ISibasiHTTPResponse<ITransactionHeader>>(
      `${environment.api_url}pos/quote/delete/${quoteId}`
    );
  }
  actionQuote(
    quoteId: string,
    action: ETransactionStatus
  ): Observable<ISibasiHTTPResponse<ITransactionHeader>> {
    return this.http.post<ISibasiHTTPResponse<ITransactionHeader>>(
      `${environment.api_url}pos/quote/delete/${quoteId}`,
      action
    );
  }
  getQuoteFromPreviousTransaction(
    transactionId: string,
    quoteDueDate: any
  ): Observable<ISibasiHTTPResponse<ITransactionHeader>> {
    return this.http.get<ISibasiHTTPResponse<ITransactionHeader>>(
      `${environment.api_url}pos/quote/transaction/${transactionId}/${quoteDueDate}`
    );
  }

  generateQuoteFromSale(
    quoteDetails: any
  ): Observable<ISibasiHTTPResponse<ITransaction>> {
    return this.http.post<ISibasiHTTPResponse<ITransaction>>(
      `${environment.api_url}pos/quote/new/posSale/covert`,
      quoteDetails
    );
  }

  getAllQuotesCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.http.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}pos/quote/count/all`
    );
  }

  getAllSearchedQuotesCount(
    searchParam: string
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.http.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}pos/quote/count/searched`,
      { searchParam }
    );
  }

  getPaginatedQuotes(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<IPagination<ITransactionHeader[]>>> {
    return this.http.post<ISibasiHTTPResponse<IPagination<ITransactionHeader[]>>>(
      `${environment.api_url}pos/quote/paginated`,
      pagination ? pagination : {}
    );
  }

  updateQuoteLineItem(
    transactionLineItem: ITransactionLineItem
  ): Observable<ISibasiHTTPResponse<ITransactionLineItem>> {
    return this.http.post<ISibasiHTTPResponse<ITransactionLineItem>>(
      `${environment.api_url}transactionLineItem/${transactionLineItem?._id}`,
      transactionLineItem
    );
  }

  deleteQuoteLineItem(
    transactionLineItem: any
  ): Observable<ISibasiHTTPResponse<ITransactionLineItem>> {
    return this.http.post<ISibasiHTTPResponse<ITransactionLineItem>>(
      `${environment.api_url}pos/quote/lineItems/delete`,
      transactionLineItem
    );
  }

  addQuoteLineItem(
    transactionLineItem: any
  ): Observable<ISibasiHTTPResponse<ITransactionLineItem>> {
    return this.http.post<ISibasiHTTPResponse<ITransactionLineItem>>(
      `${environment.api_url}pos/quote/lineItems/add`,
      transactionLineItem
    );
  }

  sendQuoteViaEmail(
    transactionId: string | undefined,
    email: any
  ): Observable<ISibasiHTTPResponse<{}>> {
    return this.http.post<ISibasiHTTPResponse<{}>>(
      `${environment.api_url}pos/quote/share/email/${transactionId}`,
      email
    );
  }

  generateTransactionUniqueCode(
    transactionType: ETransactionType
  ): Observable<ISibasiHTTPResponse<string>> {
    return this.http.get<ISibasiHTTPResponse<string>>(
      `${environment.api_url}transaction/paddedNumber/new/${transactionType}`
    );
  }

  generateAndSaveTransactionUniqueCode(
    transactionType: ETransactionType
  ): Observable<ISibasiHTTPResponse<string>> {
    return this.http.get<ISibasiHTTPResponse<string>>(
      `${environment.api_url}transaction/paddedNumber/new/save/${transactionType}`
    );
  }

  getTodaysSaleTransactionReports(
    dateRange: any
  ): Observable<ISibasiHTTPResponse<ISaleTransactionTodayReports>> {
    return this.http.post<ISibasiHTTPResponse<ISaleTransactionTodayReports>>(
      `${environment.api_url}pos/reports/organization/daily/data`,
      dateRange
    );
  }

  generateQuoteForOpportunity(
    quote: any,
    opportunityId: string
  ): Observable<ISibasiHTTPResponse<ITransaction>> {
    return this.http.post<ISibasiHTTPResponse<ITransaction>>(
      `${environment.api_url}pos/quote/opportunity/new/${opportunityId}`,
      quote
    );
  }

  // ******************************ORDERS*****************************************

  getPaginatedOrders(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<IPagination<ITransactionHeader[]>>> {
    return this.http.post<ISibasiHTTPResponse<IPagination<ITransactionHeader[]>>>(
      `${environment.api_url}online-shop/admin/transactions/all/paginated`,
      pagination
        ? {
            ...pagination,
            sortKey: 'createdAt',
            sortDirection: -1
          }
        : {}
    );
  }

  getAllOrders(): Observable<ISibasiHTTPResponse<ITransaction[]>> {
    return this.http.get<ISibasiHTTPResponse<ITransaction[]>>(
      `${environment.api_url}online-shop/admin/all/organization/transactions`
    );
  }

  getOrderById(transactionId: string): Observable<ISibasiHTTPResponse<ITransaction>> {
    return this.http.get<ISibasiHTTPResponse<ITransaction>>(
      `${environment.api_url}online-shop/admin/transactions/one/${transactionId}`
    );
  }

  // ***********************************************************************

  getCurrencyConversionRate(
    currencyToConvert: any
  ): Observable<ISibasiHTTPResponse<any>> {
    return this.http.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}currency/conversion/rate`,
      currencyToConvert
    );
  }

  getInvoiceHistory(invoiceId: string): Observable<ISibasiHTTPResponse<any>> {
    return this.http.get<ISibasiHTTPResponse<any>>(
      `${environment.api_url}transaction-status-log/transaction/${invoiceId}`
    );
  }
}
